import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar';
import { TypedMemo } from '../../common/types';
import { reviewMessages } from './reviewMessages';
import type { ReviewProblem } from './reviewRules';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the review screen; each part uses its own slice of it */
import styles from './Review.module.scss';
/* eslint-enable css-modules/no-unused-class */

const STARS = [1, 2, 3, 4, 5] as const;

interface ReviewRatingFieldProps {
  /** 0 before the user picks a star. */
  value: number;
  onChange: (rating: number) => void;
  problem: ReviewProblem | undefined;
  disabled: boolean;
}

/** The five stars (design review-form a1). A radio group, so the arrow keys work too. */
const ReviewRatingFieldComponent: React.FC<ReviewRatingFieldProps> = ({ value, onChange, problem, disabled }) => {
  const intl = useIntl();
  const label = intl.formatMessage(reviewMessages.ratingLabel);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      onChange(Math.min(5, value + 1));
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      onChange(Math.max(1, value - 1));
    }
  };

  return (
    <div className="d-flex flex-column gap-2">
      <span id="reviewRatingLabel" className="form-label mb-0">
        {label}
      </span>
      <div className={styles.stars} role="radiogroup" aria-labelledby="reviewRatingLabel">
        {STARS.map((star) => (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={value === star}
            aria-label={intl.formatMessage(reviewMessages.ratingStar, { count: star })}
            tabIndex={value === star || (value === 0 && star === 1) ? 0 : -1}
            className={clsx(styles.star, star <= value && styles.starOn)}
            onClick={() => onChange(star)}
            onKeyDown={onKeyDown}
            disabled={disabled}
          >
            <FontAwesomeIcon icon={faStar} />
          </button>
        ))}
      </div>
      {problem !== undefined && (
        <span className={styles.error} role="alert">
          {intl.formatMessage(reviewMessages.ratingMissing)}
        </span>
      )}
    </div>
  );
};

export const ReviewRatingField = TypedMemo(ReviewRatingFieldComponent);
